import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import { type RootState } from '../store'
import {
  type CrossClientQueryDecompositionResult,
  type CrossClientQueryExecution,
  type CrossClientQueryExecutionProgress,
  type CrossClientQueryInitializationResult,
} from '../../models/CrossClient'
import { type User } from '../../models/User'
import { executionHasQueuedEvaluation, executionHasRunningEvaluation } from './utils'

interface CrossClientLandingState {
  initLoading: boolean
  evaluateLoading: boolean
  decompositionResult: CrossClientQueryDecompositionResult | null
  initialization: CrossClientQueryInitializationResult | null
  runningExecutions: CrossClientQueryExecution[]
  runningExecutionProgresses: CrossClientQueryExecutionProgress[]
  runningExecutionsLoading: boolean
  pollingEnabled: boolean
  teammates: User[]
  teammatesLoading: boolean
  selectedOwnerId: string | null
}

const initialState: CrossClientLandingState = {
  initLoading: false,
  evaluateLoading: false,
  decompositionResult: null,
  initialization: null,
  runningExecutions: [],
  runningExecutionProgresses: [],
  runningExecutionsLoading: true,
  pollingEnabled: false,
  teammates: [],
  teammatesLoading: true,
  selectedOwnerId: null,
}

export const crossClientLandingSlice = createSlice({
  name: 'crossClientLanding',
  initialState,
  reducers: {
    setCrossClientInitLoading: (state, action: PayloadAction<boolean>) => {
      state.initLoading = action.payload
    },
    setCrossClientEvaluateLoading: (state, action: PayloadAction<boolean>) => {
      state.evaluateLoading = action.payload
    },

    setCrossClientLandingDecompositionResult: (state, action: PayloadAction<CrossClientQueryDecompositionResult | null>) => {
      state.decompositionResult = action.payload
    },
    setCrossClientLandingInitialization: (state, action: PayloadAction<CrossClientQueryInitializationResult | null>) => {
      state.initialization = action.payload
    },
    deleteCrossClientLandingExecution: (state, action: PayloadAction<string>) => {
      state.runningExecutions = state.runningExecutions.filter((e) => e.id !== action.payload)
      state.runningExecutionProgresses = state.runningExecutionProgresses.filter((p) => p.executionId !== action.payload)
    },
    setCrossClientLandingRunningExecutions: (state, action: PayloadAction<CrossClientQueryExecution[]>) => {
      state.runningExecutions = action.payload
    },
    setCrossClientLandingRunningExecutionProgresses: (state, action: PayloadAction<CrossClientQueryExecutionProgress[]>) => {
      state.runningExecutionProgresses = action.payload
    },
    setCrossClientLandingRunningExecutionsLoading: (state, action: PayloadAction<boolean>) => {
      state.runningExecutionsLoading = action.payload
    },

    startCrossClientLandingPolling: (state) => {
      state.pollingEnabled = true
    },
    stopCrossClientLandingPolling: (state) => {
      state.pollingEnabled = false
    },
    setCrossClientLandingTeammates: (state, action: PayloadAction<User[]>) => {
      state.teammates = action.payload
    },
    setCrossClientLandingTeammatesLoading: (state, action: PayloadAction<boolean>) => {
      state.teammatesLoading = action.payload
    },
    setCrossClientLandingSelectedOwnerId: (state, action: PayloadAction<string | null>) => {
      state.selectedOwnerId = action.payload
      // Clear the owner-scoped lists and mark loading so the panels show their loading state during the
      // refetch instead of briefly flashing the empty-states with the cleared lists.
      state.runningExecutions = []
      state.runningExecutionProgresses = []
      state.runningExecutionsLoading = true
    },
    resetCrossClientLanding: (state) => ({
      ...initialState,
      teammates: state.teammates,
      teammatesLoading: state.teammatesLoading,
      // Keep the admin owner filter across navigation/back-button so it isn't cleared on remount.
      selectedOwnerId: state.selectedOwnerId,
    }),
  },
})

export const {
  setCrossClientInitLoading,
  setCrossClientEvaluateLoading,
  setCrossClientLandingDecompositionResult,
  setCrossClientLandingInitialization,
  startCrossClientLandingPolling,
  stopCrossClientLandingPolling,
  setCrossClientLandingRunningExecutions,
  setCrossClientLandingRunningExecutionProgresses,
  setCrossClientLandingRunningExecutionsLoading,
  deleteCrossClientLandingExecution,
  setCrossClientLandingTeammates,
  setCrossClientLandingTeammatesLoading,
  setCrossClientLandingSelectedOwnerId,
  resetCrossClientLanding,
} = crossClientLandingSlice.actions

export const selectCrossClientInitLoading = (state: RootState) => state.crossClientLanding.initLoading

export const selectCrossClientEvaluateLoading = (state: RootState) => state.crossClientLanding.evaluateLoading

export const selectCrossClientLandingDecompositionResult = (state: RootState) => state.crossClientLanding.decompositionResult

export const selectCrossClientLandingInitialization = (state: RootState) => state.crossClientLanding.initialization

export const selectCrossClientLandingPollingEnabled = (state: RootState) => state.crossClientLanding.pollingEnabled

export const selectCrossClientLandingRunningExecutions = (state: RootState) => state.crossClientLanding.runningExecutions

export const selectCrossClientLandingQueuedExecutions = createSelector([selectCrossClientLandingRunningExecutions], (runningExecutions) =>
  runningExecutions.filter((e) => executionHasQueuedEvaluation(e)),
)

export const selectCrossClientLandingPollableExecutions = createSelector([selectCrossClientLandingRunningExecutions], (runningExecutions) =>
  runningExecutions.filter((e) => executionHasRunningEvaluation(e)),
)

export const selectCrossClientLandingRunningCount = createSelector(
  [selectCrossClientLandingPollableExecutions, selectCrossClientLandingQueuedExecutions],
  (pollable, queued) => pollable.length + queued.length,
)

export const selectCrossClientLandingRunningExecutionProgresses = (state: RootState) => state.crossClientLanding.runningExecutionProgresses

export const selectCrossClientLandingRunningExecutionsLoading = (state: RootState) => state.crossClientLanding.runningExecutionsLoading

export const selectCrossClientLandingTeammates = (state: RootState) => state.crossClientLanding.teammates

export const selectCrossClientLandingTeammatesLoading = (state: RootState) => state.crossClientLanding.teammatesLoading

export const selectCrossClientLandingSelectedOwnerId = (state: RootState) => state.crossClientLanding.selectedOwnerId

export default crossClientLandingSlice.reducer
